// @ts-check
import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { Container, Grid, Divider, Typography } from '@material-ui/core';
import { getSelectedValue, mapProductData, toTitleCase } from 'utils';
import { ORDER_CONFIRMATION_TYPE, BG_ELEVATED_PACKAGING_THRESHOLD_PRICE } from 'constants/index';
import MainHeader from '../MainHeader';
import Button from '../Button';
import PageLayout from '../PageLayout';
import ProductInfo from '../ProductInfo';
import FloatingControlsContainer from '../FloatingControlsContainer';
import GeneralOrderInfo from '../GeneralOrderInfo';
import PricingInfo from '../PricingInfo';
import { CloseIcon } from '../Icons';
import { totalAmountForElevatedPackaging } from '../../modules/clients/utils';
import { useHideNavigation } from '../../modules/shell/hooks';

const OrderConfirmation = ({
  classes,
  history,
  orderId,
  transactionType,
  productsData,
  clientData,
  shipmentMethod,
  mappedShipmentData,
  shippingAddress,
  storeAddressInfo,
  selectedPayments,
  selectedGCPayments,
  selectedPaymentsAmount,
  amountReceived,
  bopsAddress,
  isBopsOrder,
  showBottomButton,
  brand,
  confirmationType,
}) => {
  const firstName = clientData?.firstName;
  const lastName = clientData?.lastName;
  const email = clientData?.email;
  const shipmentMethodName = shipmentMethod?.name || shipmentMethod?.shipmentName;
  const isVoid = confirmationType === ORDER_CONFIRMATION_TYPE.VOID;
  const isReturn = confirmationType === ORDER_CONFIRMATION_TYPE.RETURN;
  const isGiftReceipt = confirmationType === ORDER_CONFIRMATION_TYPE.GIFT;
  const selectedShippingAddress = shippingAddress || getSelectedValue(clientData?.shippingAddress);
  const shipmentData = mappedShipmentData || shipmentMethod;
  const navigateToHomePage = () => history.push('/');
  const isBgUser = brand === 'BG';
  const isBgElevatedPackagingEligible =
    isBgUser && totalAmountForElevatedPackaging(productsData) >= BG_ELEVATED_PACKAGING_THRESHOLD_PRICE;
  useHideNavigation(!isGiftReceipt);

  const renderProductsInfo = () => {
    return productsData.map((data) => {
      const mappedData = mapProductData(data);
      return (
        <Container
          classes={{ root: cx(classes.productInfoContainer, classes.borderBottom) }}
          key={mappedData?.pimSkuId}
        >
          <ProductInfo
            productData={mappedData}
            isBopsOrder={isBopsOrder}
            bopsAddress={bopsAddress}
            orderConfirmationView
            isGiftReceipt
          />
        </Container>
      );
    });
  };

  return (
    <>
      <MainHeader
        title={isGiftReceipt ? 'Gift Receipt Sent' : 'Order confirmation'}
        closeIcon={CloseIcon}
        onClose={navigateToHomePage}
        divider
      />
      <PageLayout classes={{ content: showBottomButton ? classes.layoutWithButton : classes.layoutContent }}>
        {isGiftReceipt ? (
          <>
            <Typography data-testid="title-message-paragraph" paragraph classes={{ root: classes.giftDescription }}>
              A gift receipt has been created for {toTitleCase(firstName)} {toTitleCase(lastName)} and sent to
              <span className={classes.email}> {email}</span>.
            </Typography>
            <Divider />
          </>
        ) : (
          <GeneralOrderInfo
            orderId={orderId}
            email={email}
            transactionType={transactionType}
            shippingAddress={selectedShippingAddress}
            storeAddressInfo={storeAddressInfo}
            bopsAddress={bopsAddress}
            shipmentMethodName={shipmentMethodName}
            firstName={firstName}
            lastName={lastName}
            isVoid={isVoid}
            isReturn={isReturn}
            isBopsOrder={isBopsOrder}
          />
        )}
        {renderProductsInfo()}
        {!isGiftReceipt && (
          <>
            <Divider />
            <PricingInfo
              shipmentData={shipmentData}
              transactionType={transactionType}
              selectedPayments={selectedPayments}
              selectedGCPayments={selectedGCPayments}
              selectedPaymentsAmount={selectedPaymentsAmount}
              cardAmountReceived={amountReceived}
              isBgElevatedPackagingEligible={isBgElevatedPackagingEligible}
              isVoidOrder={isVoid}
              isReturnOrder={isReturn}
            />
          </>
        )}
      </PageLayout>
      {showBottomButton ||
        (isGiftReceipt && (
          <FloatingControlsContainer>
            <Grid item xs={12}>
              <Button label="Done" onClick={navigateToHomePage} />
            </Grid>
          </FloatingControlsContainer>
        ))}
    </>
  );
};

OrderConfirmation.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string),
  history: PropTypes.shape({
    push: PropTypes.func,
  }),
  orderId: PropTypes.string,
  transactionType: PropTypes.string,
  clientData: PropTypes.shape({}),
  productsData: PropTypes.arrayOf(PropTypes.shape({})),
  shipmentMethod: PropTypes.shape({}),
  mappedShipmentData: PropTypes.shape({}),
  shippingAddress: PropTypes.shape({}),
  bopsAddress: PropTypes.shape({}),
  storeAddressInfo: PropTypes.shape({}),
  selectedPayments: PropTypes.arrayOf(PropTypes.object),
  selectedPaymentsAmount: PropTypes.shape({}),
  amountReceived: PropTypes.string,
  isBopsOrder: PropTypes.bool,
  confirmationType: PropTypes.string,
  brand: PropTypes.string,
  showBottomButton: PropTypes.bool,
};

OrderConfirmation.defaultProps = {
  classes: {},
  productsData: [],
  selectedPaymentsAmount: {},
  showBottomButton: false,
};

export default OrderConfirmation;
